import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { StatCard, StatusBadge, ProgressBar } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { money, num, fmtDateTime, DELIVERY_STATUS, deliveryLabel } from "@/lib/format";
import { toast } from "sonner";
import {
  Trophy, Eye, EyeOff, Handshake, Target, DollarSign, BarChart3, Download,
  ShieldAlert, Palette, Building2, CheckCircle2, Users, Ticket, MessageCircle, Pencil, Plus, Trash2, Upload, X, Landmark,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const DELIVERY = ["Pendiente", "En proceso", "Entregado"];
const BLANK_W = { name_public: "", city: "", prize: "", winning_number: "", raffle_name: "", date: "", year: new Date().getFullYear(), testimonial: "", delivery_status: "Pendiente", delivered_at: null, gallery: [], published: false };

export function AdminWinners() {
  const [winners, setWinners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [w, setW] = useState(BLANK_W);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/admin/winners").then((r) => { setWinners(r.data); return r.data; });
  useEffect(() => {
    load().then((list) => {
      const id = new URLSearchParams(window.location.search).get("edit");
      const x = id && list.find((y) => y.id === id);
      if (x) openEdit(x);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setW({ ...w, [k]: e.target.value });
  const openNew = () => { setEditing(null); setW(BLANK_W); setOpen(true); };
  const openEdit = (x) => { setEditing(x.id); setW({ ...BLANK_W, ...x }); setOpen(true); };
  const toggle = async (x) => {
    await api.put(`/admin/winners/${x.id}/publish`, { published: !x.published });
    toast.success(x.published ? "Ganador ocultado" : "Ganador publicado");
    await load();
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/raffles/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setW((p) => ({ ...p, gallery: [...p.gallery, r.data.url] }));
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo subir la foto"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const save = async () => {
    if (!w.name_public.trim() || !w.prize.trim() || !w.winning_number.trim()) { toast.error("Nombre, premio y número ganador son obligatorios"); return; }
    const payload = { ...w, year: Number(w.year) || new Date().getFullYear() };
    try {
      if (editing) { await api.put(`/admin/winners/${editing}`, payload); toast.success("Ganador actualizado"); }
      else { await api.post("/admin/winners", payload); toast.success("Ganador creado"); }
      setOpen(false); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
  };
  const remove = async (x) => {
    if (!window.confirm(`¿Eliminar al ganador "${x.name_public}"?`)) return;
    try { await api.delete(`/admin/winners/${x.id}`); toast.success("Ganador eliminado"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
  };
  return (
    <AdminLayout title="Ganadores">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-slate-500">{winners.length} ganadores registrados</p>
        <Button onClick={openNew} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-winner-btn"><Plus className="mr-1.5 h-4 w-4" />Nuevo ganador</Button>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {winners.map((x) => (
          <div key={x.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`admin-winner-${x.id}`}>
            {x.gallery?.[0] ? <img src={x.gallery[0]} alt="" className="aspect-video w-full object-cover" /> : <div className="grid aspect-video w-full place-items-center bg-slate-100 text-slate-400"><Trophy className="h-8 w-8" /></div>}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900">{x.name_public}</h3>
                {x.published ? <StatusBadge label="Publicado" cls="bg-emerald-50 text-emerald-700" /> : <StatusBadge label="Bajo revisión" cls="bg-amber-50 text-amber-700" />}
              </div>
              <p className="mt-1 text-sm text-slate-500">{x.prize} · #{x.winning_number}</p>
              <p className="text-xs text-slate-400">{x.raffle_name}{x.date ? ` · ${x.date}` : ""}</p>
              <span className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${DELIVERY_STATUS[x.delivery_status] || DELIVERY_STATUS.Pendiente}`} data-testid={`winner-delivery-${x.id}`}>{deliveryLabel(x)}</span>
              {x.customer?.email || x.customer?.whatsapp ? <p className="mt-1 truncate text-xs text-slate-500" data-testid={`winner-contact-${x.id}`}>{[x.customer.email, x.customer.whatsapp].filter(Boolean).join(" · ")}</p> : null}
              {!x.gallery?.length && <p className="mt-2 text-xs font-medium text-amber-600">Falta la foto del ganador</p>}
              <div className="mt-4 flex gap-2">
                <Button onClick={() => toggle(x)} variant="outline" size="sm" className="flex-1" data-testid={`toggle-winner-${x.id}`}>
                  {x.published ? <><EyeOff className="mr-1.5 h-4 w-4" />Ocultar</> : <><Eye className="mr-1.5 h-4 w-4" />Publicar</>}
                </Button>
                <Button onClick={() => openEdit(x)} variant="outline" size="sm" data-testid={`edit-winner-${x.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button onClick={() => remove(x)} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-winner-${x.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="winner-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar ganador" : "Nuevo ganador"}</DialogTitle><DialogDescription>Datos que se mostrarán en la sección pública de ganadores.</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Nombre público *</Label><Input value={w.name_public} onChange={set("name_public")} placeholder="José R." data-testid="w-name" /></div>
            <div><Label>Ciudad</Label><Input value={w.city} onChange={set("city")} placeholder="Guadalajara, Jal." data-testid="w-city" /></div>
            <div><Label>Premio *</Label><Input value={w.prize} onChange={set("prize")} placeholder="Automóvil Sedán 2026" data-testid="w-prize" /></div>
            <div><Label>Número ganador *</Label><Input value={w.winning_number} onChange={set("winning_number")} placeholder="3841" data-testid="w-number" /></div>
            <div><Label>Nombre del sorteo</Label><Input value={w.raffle_name} onChange={set("raffle_name")} placeholder="Gran Sorteo 2026" data-testid="w-raffle" /></div>
            <div><Label>Fecha del sorteo</Label><Input value={w.date} onChange={set("date")} placeholder="12 de diciembre de 2026" data-testid="w-date" /></div>
            <div><Label>Año</Label><Input type="number" value={w.year} onChange={set("year")} data-testid="w-year" /></div>
            <div><Label>Estado de entrega</Label>
              <Select value={w.delivery_status} onValueChange={(v) => setW({ ...w, delivery_status: v, delivered_at: v === "Entregado" ? (w.delivered_at || new Date().toISOString().slice(0, 10)) : null })}>
                <SelectTrigger data-testid="w-delivery"><SelectValue /></SelectTrigger>
                <SelectContent>{DELIVERY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {w.delivery_status === "Entregado" && <div><Label>Fecha de entrega</Label><Input type="date" value={w.delivered_at || ""} onChange={set("delivered_at")} data-testid="w-delivered-at" /></div>}
            <div className="sm:col-span-2"><Label>Testimonio</Label><Textarea rows={3} value={w.testimonial} onChange={set("testimonial")} placeholder="Palabras del ganador…" data-testid="w-testimonial" /></div>
            <div className="sm:col-span-2">
              <Label>Fotos</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {w.gallery.map((u, i) => (
                  <div key={u} className="relative h-24 w-32 overflow-hidden rounded-lg border border-slate-200">
                    <img src={u} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setW({ ...w, gallery: w.gallery.filter((_, j) => j !== i) })} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white" data-testid={`w-photo-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <label className="grid h-24 w-32 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500 hover:border-brand hover:text-brand">
                  <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="w-photo-input" />
                  <span className="flex flex-col items-center gap-1"><Upload className="h-5 w-5" />{uploading ? "Subiendo…" : "Subir foto"}</span>
                </label>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:col-span-2"><Switch checked={w.published} onCheckedChange={(v) => setW({ ...w, published: v })} data-testid="w-published" /><Label>Publicar en el sitio público</Label></div>
          </div>
          <div className="mt-4 flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-brand hover:bg-brand-hover" data-testid="w-save">{editing ? "Guardar cambios" : "Crear ganador"}</Button></div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

export function Sellers() {
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("month");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ goal: 0, commission_type: "percent", commission_rate: 8, commission_per_ticket: 0, tiers: [] });
  const load = () => api.get(`/admin/sellers/ranking?period=${period}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [period]);
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      goal: s.goal || 0,
      commission_type: s.commission_type || "percent",
      commission_rate: Math.round((s.commission_rate ?? 0.08) * 100),
      commission_per_ticket: s.commission_per_ticket || 0,
      tiers: (s.tiers || []).length ? s.tiers.map((t) => ({ tickets: t.tickets, rate: Math.round(t.rate * 100) })) : [{ tickets: 0, rate: 5 }, { tickets: 1000, rate: 8 }, { tickets: 2000, rate: 10 }],
    });
  };
  const setTier = (i, k, v) => setForm({ ...form, tiers: form.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/sellers/${editing.code}`, {
        goal: Number(form.goal) || 0,
        commission_type: form.commission_type,
        commission_rate: (Number(form.commission_rate) || 0) / 100,
        commission_per_ticket: Number(form.commission_per_ticket) || 0,
        tiers: form.commission_type === "tiered" ? form.tiers.map((t) => ({ tickets: Number(t.tickets) || 0, rate: (Number(t.rate) || 0) / 100 })) : [],
      });
      toast.success("Vendedor actualizado");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    }
  };
  const commissionLabel = (s) => s.commission_type === "per_ticket"
    ? `${money(s.commission_per_ticket || 0)}/boleto`
    : s.commission_type === "tiered"
      ? `Nivel ${s.level?.level || 0}/${s.level?.levels || 0} · ${Math.round((s.level?.current?.rate || 0) * 100)}%`
      : `${((s.commission_rate || 0) * 100).toFixed(0)}%`;
  const totalRevenue = rows.reduce((a, r) => a + (r.revenue || 0), 0);
  const totalCommission = rows.reduce((a, r) => a + (r.commission_earned || 0), 0);
  const totalSales = rows.reduce((a, r) => a + (r.sales_total || 0), 0);
  const medal = (i) => i === 0 ? "bg-amber-400 text-white" : i === 1 ? "bg-slate-300 text-white" : i === 2 ? "bg-amber-700 text-white" : "bg-slate-100 text-slate-500";
  const downloadCSV = () => {
    const headers = ["Posición", "Vendedor", "Código", "Comisión", "Ventas aprobadas", "Ventas del mes", "Meta", "Ingresos", "Comisión ganada", "Comisión por confirmar", "Conversión %", "Clientes"];
    const lines = rows.map((s, i) => [i + 1, s.name, s.code, commissionLabel(s), s.sales_total, s.sales_month, s.goal, s.revenue, s.commission_earned, s.commission_pending, s.conversion, s.clients]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...lines].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `ranking-vendedores-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Ranking exportado en CSV");
  };
  return (
    <AdminLayout title="Vendedores">
      <div className="mb-5 flex items-center gap-2">
        <span className="text-sm text-slate-500">Periodo:</span>
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="period-filter">
          {[["month", "Este mes"], ["quarter", "Trimestre"], ["all", "Total"]].map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)} data-testid={`period-${v}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${period === v ? "bg-brand text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        <StatCard label="Vendedores" value={num(rows.length)} icon={Handshake} />
        <StatCard label="Ventas aprobadas" value={num(totalSales)} icon={Trophy} />
        <StatCard label="Ingresos generados" value={money(totalRevenue)} icon={DollarSign} />
        <StatCard label="Comisiones ganadas" value={money(totalCommission)} tone="success" icon={DollarSign} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="sellers-ranking">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4"><Trophy className="h-4 w-4 text-brand" /><h3 className="font-semibold text-slate-900">Ranking de vendedores</h3><span className="ml-auto hidden text-xs text-slate-400 sm:inline">Ordenado por ingresos generados</span><Button variant="outline" size="sm" className="ml-auto sm:ml-3" onClick={downloadCSV} disabled={rows.length === 0} data-testid="export-ranking-csv"><Download className="mr-1.5 h-4 w-4" />Exportar CSV</Button></div>
        {rows.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">Aún no hay vendedores registrados. Crea uno en la sección Usuarios.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-4 py-3">#</th><th className="px-4 py-3">Vendedor</th>
                  <th className="px-4 py-3 text-right">Ventas</th><th className="px-4 py-3 text-right">Mes</th>
                  <th className="px-4 py-3 w-40">Meta</th><th className="px-4 py-3 text-right">Ingresos</th>
                  <th className="px-4 py-3 text-right">Comisión</th><th className="px-4 py-3 text-right">Por confirmar</th>
                  <th className="px-4 py-3 text-right">Conv.</th><th className="px-4 py-3 text-right">Clientes</th>
                  <th className="px-4 py-3 text-right">Editar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((s, i) => {
                  const pct = s.goal ? Math.min(100, Math.round((s.sales_month / s.goal) * 100)) : 0;
                  return (
                    <tr key={s.code} data-testid={`ranking-row-${s.code}`} className={i === 0 ? "bg-amber-50/40" : ""}>
                      <td className="px-4 py-3"><span className={`inline-grid h-7 w-7 place-items-center rounded-full text-xs font-bold ${medal(i)}`}>{i + 1}</span></td>
                      <td className="px-4 py-3"><div className="font-semibold text-slate-900">{s.name}</div><div className="font-mono text-xs text-slate-400">{s.code} · {commissionLabel(s)}</div></td>
                      <td className="px-4 py-3 text-right font-mono">{num(s.sales_total)}</td>
                      <td className="px-4 py-3 text-right font-mono">{num(s.sales_month)}</td>
                      <td className="px-4 py-3"><ProgressBar value={pct} /><div className="mt-1 font-mono text-xs text-slate-400">{s.sales_month}/{s.goal} · {pct}%</div></td>
                      <td className="px-4 py-3 text-right font-mono">{money(s.revenue)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{money(s.commission_earned)}</td>
                      <td className="px-4 py-3 text-right font-mono text-amber-600">{money(s.commission_pending)}</td>
                      <td className="px-4 py-3 text-right font-mono">{s.conversion}%</td>
                      <td className="px-4 py-3 text-right font-mono">{num(s.clients)}</td>
                      <td className="px-4 py-3 text-right"><Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-seller-${s.code}`}><Pencil className="h-4 w-4 text-slate-500" /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="seller-config-dialog">
          <DialogHeader>
            <DialogTitle>Meta y comisión · {editing?.name}</DialogTitle>
            <DialogDescription>Define la meta mensual y el esquema de comisión de este vendedor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Meta de ventas del mes (boletos)</Label>
              <Input type="number" min={0} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="mt-1.5 font-mono" data-testid="seller-goal-input" disabled={form.commission_type === "tiered"} />
              {form.commission_type === "tiered" && <p className="mt-1 text-xs text-slate-400">Con niveles, la meta es automáticamente el siguiente umbral.</p>}
            </div>
            <div>
              <Label>Tipo de comisión</Label>
              <div className="mt-1.5 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="commission-type-toggle">
                {[["percent", "Porcentaje"], ["per_ticket", "Por boleto"], ["tiered", "Por niveles"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, commission_type: v })}
                    data-testid={`commission-type-${v}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${form.commission_type === v ? "bg-brand text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
                ))}
              </div>
            </div>
            {form.commission_type === "percent" && (
              <div>
                <Label>Comisión (% sobre ventas aprobadas)</Label>
                <Input type="number" min={0} max={100} value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="mt-1.5 font-mono" data-testid="seller-rate-input" />
              </div>
            )}
            {form.commission_type === "per_ticket" && (
              <div>
                <Label>Comisión por boleto (MXN)</Label>
                <Input type="number" min={0} value={form.commission_per_ticket} onChange={(e) => setForm({ ...form, commission_per_ticket: e.target.value })} className="mt-1.5 font-mono" data-testid="seller-perticket-input" />
              </div>
            )}
            {form.commission_type === "tiered" && (
              <div data-testid="tiers-editor">
                <Label>Niveles por boletos vendidos en el mes</Label>
                <p className="mt-1 text-xs text-slate-500">Al alcanzar el umbral se libera el % de ese nivel para todas las ventas del mes. El siguiente umbral se convierte en la meta del vendedor.</p>
                <div className="mt-2 space-y-2">
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-semibold uppercase text-slate-400"><span>Desde (boletos)</span><span>Comisión %</span><span /></div>
                  {form.tiers.map((t, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                      <Input type="number" min={0} value={t.tickets} onChange={(e) => setTier(i, "tickets", e.target.value)} className="font-mono" data-testid={`tier-tickets-${i}`} />
                      <Input type="number" min={0} max={100} value={t.rate} onChange={(e) => setTier(i, "rate", e.target.value)} className="font-mono" data-testid={`tier-rate-${i}`} />
                      <Button type="button" variant="outline" size="icon" onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, j) => j !== i) })} data-testid={`tier-remove-${i}`}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, tiers: [...form.tiers, { tickets: (Number(form.tiers.at(-1)?.tickets) || 0) + 1000, rate: (Number(form.tiers.at(-1)?.rate) || 0) + 2 }] })} data-testid="tier-add"><Plus className="mr-1 h-3.5 w-3.5" />Agregar nivel</Button>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} className="bg-brand hover:bg-brand-hover" data-testid="seller-config-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

export function Reports() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/reports").then((r) => setD(r.data)).catch(() => {}); }, []);
  const sales = (d?.sales_by_month || []).map((x) => ({ m: x.m, v: x.revenue }));
  const perf = d?.seller_performance || [];
  const exportCSV = () => {
    const rows = [["Mes", "Año", "Ventas", "Ingresos"], ...(d?.sales_by_month || []).map((x) => [x.m, x.year, x.sales, x.revenue])];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `reporte-ventas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Reporte exportado en CSV");
  };
  return (
    <AdminLayout title="Reportes">
      <div className="mb-6 flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Ingresos y ventas reales por mes · filtrado por empresa</p>
        <Button variant="outline" onClick={exportCSV} disabled={!d} data-testid="export-report"><Download className="mr-1.5 h-4 w-4" />Exportar CSV</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Ingresos (aprobados)" value={money(d?.revenue || 0)} icon={DollarSign} />
        <StatCard label="Ticket promedio" value={money(d?.avg_ticket || 0)} icon={Ticket} />
        <StatCard label="Clientes recurrentes" value={`${d?.recurring_pct || 0}%`} icon={Users} />
        <StatCard label="Conversión" value={`${d?.conversion || 0}%`} icon={BarChart3} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="report-sales-chart">
          <h3 className="font-semibold text-slate-900">Ingresos por mes</h3>
          <div className="mt-6 h-64"><ResponsiveContainer><LineChart data={sales}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="m" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} /><YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} /><Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(v) => money(v)} /><Line type="monotone" dataKey="v" stroke="#1D4ED8" strokeWidth={2.5} dot={{ r: 3 }} /></LineChart></ResponsiveContainer></div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900">Rendimiento por vendedor</h3>
          {perf.length === 0 ? (
            <div className="mt-6 grid h-64 place-items-center text-sm text-slate-400">Sin ventas atribuidas aún</div>
          ) : (
            <div className="mt-6 h-64"><ResponsiveContainer><BarChart data={perf}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="n" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} /><Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} /><Bar dataKey="v" fill="#10B981" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer></div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

const ACTION_STYLE = {
  "payment.approve": "text-emerald-600", "payment.reject": "text-red-600", "payment.correction": "text-amber-600",
};

export function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <AdminLayout title="Auditoría">
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">
        <ShieldAlert className="h-4 w-4 text-brand" />Registro inmutable de acciones sensibles. Cada evento incluye usuario, fecha, estado anterior y nuevo.
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr><th className="px-5 py-3">Fecha</th><th className="px-5 py-3">Usuario</th><th className="px-5 py-3">Acción</th><th className="px-5 py-3">Entidad</th><th className="px-5 py-3">Motivo</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {logs.map((l) => (
              <tr key={l.id} data-testid="audit-row">
                <td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(l.created_at)}</td>
                <td className="px-5 py-3 font-medium text-slate-700">{l.user}</td>
                <td className={`px-5 py-3 font-mono text-xs font-semibold ${ACTION_STYLE[l.action] || "text-slate-600"}`}>{l.action}</td>
                <td className="px-5 py-3 font-mono text-xs text-slate-500">{l.entity_id}</td>
                <td className="px-5 py-3 text-xs text-slate-400">{l.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

export function Branding() {
  const [s, setS] = useState({ reservation_minutes: 30, whatsapp: "", socials: {} });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS({
      reservation_minutes: r.data.reservation_minutes ?? 30,
      whatsapp: r.data.whatsapp || "",
      socials: r.data.socials || {},
    })).catch(() => {});
  }, []);
  const setSocial = (k, v) => setS((p) => ({ ...p, socials: { ...p.socials, [k]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        reservation_minutes: Number(s.reservation_minutes) || 30,
        whatsapp: s.whatsapp,
        socials: s.socials,
      });
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };
  const SOCIALS = [
    ["facebook", "Facebook"], ["instagram", "Instagram"], ["tiktok", "TikTok"],
    ["youtube", "YouTube"], ["website", "Sitio web"],
  ];
  return (
    <AdminLayout title="Configuración de la empresa">
      <div className="max-w-3xl space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Building2 className="h-4 w-4 text-brand" />Operación</h3>
          <p className="mt-1 text-sm text-slate-500">Aplica a las nuevas reservas de tus sorteos.</p>
          <div className="mt-4 max-w-xs">
            <Label>Tiempo de reserva de boletos (minutos)</Label>
            <Input type="number" min={5} max={1440} value={s.reservation_minutes}
              onChange={(e) => setS({ ...s, reservation_minutes: e.target.value })}
              className="mt-1.5 font-mono" data-testid="settings-reservation-minutes" />
            <p className="mt-1.5 text-xs text-slate-400">Los boletos se liberan si no se sube comprobante dentro de este tiempo (5–1440 min).</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-semibold text-slate-900"><MessageCircle className="h-4 w-4 text-brand" />Contacto y redes sociales</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2 max-w-sm">
              <Label>WhatsApp oficial</Label>
              <Input value={s.whatsapp} onChange={(e) => setS({ ...s, whatsapp: e.target.value })}
                placeholder="+52 33 1234 5678" className="mt-1.5" data-testid="settings-whatsapp" />
            </div>
            {SOCIALS.map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input value={s.socials?.[k] || ""} onChange={(e) => setSocial(k, e.target.value)}
                  placeholder="https://…" className="mt-1.5" data-testid={`settings-social-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <BankAccounts />
        <Button onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="save-settings">
          {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>
    </AdminLayout>
  );
}

const BLANK_BANK = { bank: "", holder: "", account: "", clabe: "", active: true };
function BankAccounts() {
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [b, setB] = useState(BLANK_BANK);
  const load = () => api.get("/admin/bank-accounts").then((r) => setBanks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setB({ ...b, [k]: e.target.value });
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/bank-accounts/${editing}`, b); else await api.post("/admin/bank-accounts", b);
      toast.success(editing ? "Cuenta actualizada" : "Cuenta agregada"); setOpen(false); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
  };
  const toggle = async (x) => { await api.put(`/admin/bank-accounts/${x.id}`, { ...x, active: !x.active }); await load(); };
  const remove = async (x) => {
    if (!window.confirm(`¿Eliminar la cuenta de ${x.bank}?`)) return;
    await api.delete(`/admin/bank-accounts/${x.id}`); toast.success("Cuenta eliminada"); await load();
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="bank-accounts-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Landmark className="h-4 w-4 text-brand" />Cuentas para depósitos</h3>
          <p className="mt-1 text-sm text-slate-500">Se muestran al cliente al momento de pagar. Solo las activas son visibles.</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setB(BLANK_BANK); setOpen(true); }} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-bank-btn"><Plus className="mr-1 h-4 w-4" />Agregar</Button>
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        {banks.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Aún no hay cuentas registradas. Los clientes no podrán ver a dónde depositar.</p>}
        {banks.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`bank-${x.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 font-semibold text-slate-900">{x.bank}{!x.active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Inactiva</span>}</div>
              <div className="text-sm text-slate-600">{x.holder}</div>
              <div className="font-mono text-xs text-slate-500">{x.clabe ? `CLABE ${x.clabe}` : ""}{x.clabe && x.account ? " · " : ""}{x.account ? `Cuenta ${x.account}` : ""}</div>
            </div>
            <Switch checked={!!x.active} onCheckedChange={() => toggle(x)} data-testid={`bank-active-${x.id}`} />
            <Button variant="outline" size="sm" onClick={() => { setEditing(x.id); setB({ ...BLANK_BANK, ...x }); setOpen(true); }} data-testid={`bank-edit-${x.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => remove(x)} data-testid={`bank-delete-${x.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bank-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar cuenta" : "Nueva cuenta bancaria"}</DialogTitle><DialogDescription>Datos que verá el cliente para transferir.</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Banco *</Label><Input value={b.bank} onChange={set("bank")} placeholder="BBVA" data-testid="bank-name" /></div>
            <div><Label>Titular *</Label><Input value={b.holder} onChange={set("holder")} placeholder="Círculo Dorado S.A. de C.V." data-testid="bank-holder" /></div>
            <div><Label>CLABE (18 dígitos)</Label><Input value={b.clabe} onChange={set("clabe")} maxLength={18} className="font-mono" data-testid="bank-clabe" /></div>
            <div><Label>Número de cuenta</Label><Input value={b.account} onChange={set("account")} className="font-mono" data-testid="bank-account" /></div>
          </div>
          <div className="mt-4 flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-brand hover:bg-brand-hover" data-testid="bank-save">Guardar</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
